// The face maker (PLAN-2026-10-24-the-face-maker §6, B-488): a face for a
// team's badge, built from the site's parts and painted, opened from Edit
// team's *Face*. A toy first: a child should spend ten happy minutes in it
// (DIRECTION U25).
//
// ONE QUESTION: what does my face look like? So the face is always in sight,
// large, and everything else changes it: the parts by kind, each a
// grid of its choices drawn on your own face (cropped to the part where it is
// small, as Mii's are), the colors of the areas the chosen part has, and
// steppers where a part can move.
//
// SHUFFLE IS THE FAVORITE (PLAN-2026-10-24-faces-round-two §1), so it has a
// tab of its own: shuffle as a theme, which the toolbar's Shuffle keeps
// shuffling in, and locks that keep a part, its colors and its steps. The
// faces it made wait in a strip under the face, and a swipe across the face
// shuffles too.
//
// THE WHOLE CHARACTER (PLAN-2026-10-24-faces-round-three §2.3): the Outfit
// and Hold tabs show it standing on its stage, the face's own tabs the badge,
// and the person switches between them whenever they like. The number is set
// on the Outfit tab, for an outfit that carries one.
//
// MIX WITH another face (§6): the face on screen and a favorite, another of
// your teams' or a bot's make a child, each part from one or the other, into
// the strip like any shuffle; a lock holds through a mix too.
//
// A FACE WORTH KEEPING IS A FAVORITE (PLAN-2026-10-24-faces-round-three §1):
// the star keeps the face on screen on the account, and the Favorites tab and
// Start from put one back on, so trying more ideas never loses it.
//
// TWO WAYS TO COLOR ANYTHING. Under each part, its areas' swatches; and the
// paint can, which fills whatever is tapped on the face, the team's disc and
// ring included. The paint can's panel lists the face's areas as buttons too,
// so a keyboard and a screen reader fill the same areas a finger does.
//
// DONE KEEPS IT. Done hands the face and the team's colors back to Edit team,
// which saves them at once (a face is minutes of work, and a sheet closed
// after Done must not lose it); Cancel, or closing with changes made, asks
// first, for the same reason.
//
// NO NUMBERS, so it is the same maker at every detail level.

import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import {
  BOT_FACES,
  EYE_COLORS,
  FACE_ADJUST_RANGES,
  FACE_CATEGORIES,
  FACE_MARKS_MAX,
  FACE_MOODS,
  FACE_SLOT_NAMES,
  FIGURE_CATEGORIES,
  HAIR_COLORS,
  NUMBERED_OUTFITS,
  PAINT_COLORS,
  SKIN_TONES,
  botFace,
  chooseFacePart,
  describeFace,
  facePart,
  facePartOffered,
  faceSeason,
  faceSlotsInUse,
  faceOutfit,
  keepLocked,
  mixFaces,
  faceTheme,
  faceThemesOn,
  figureColors,
  paintFace,
  paletteFor,
  randomFace,
  sameColors,
  sameFace,
  seededRng,
  shuffleFaceColors,
  shuffleFacePart,
  stepFace,
  wearOutfit,
  withoutUnearned,
  type Face,
  type FaceAdjustKey,
  type FaceArea,
  type FaceCategory,
  type FaceMood,
  type FaceThemeId,
  type NamedColor,
  type TeamColors,
} from '@fsp/domain';
import { useFaceFavorites, type FaceFavoritesRead } from '../../data/faceFavorites';
import { FACE_PART_VIEW, FaceArt } from '../../kit/FaceArt';
import { FIGURE_PART_VIEW, FigureArt } from '../../kit/FigureArt';
import { Icon, type IconName } from '../../kit/Icon';
import { Workshop } from '../../kit/overlays';
import {
  Button,
  Colored,
  Dialog,
  IconButton,
  Line,
  Stack,
  Text,
  TextField,
  tell,
  tellWithUndo,
} from '../../kit/ui';
import '../../styles/screens/face-maker.css';

/** What the maker is making: the face, and the team's two colors its disc and ring wear. */
interface Made {
  face: Face;
  colors: TeamColors;
}

/** A face to start from, with what it is called. */
export interface StartingFace {
  name: string;
  face: Face;
}

/** A color, handed to the stylesheet as data, filling whatever holds it. */
function Dot({ hex }: { hex: string }) {
  return <Colored inline className="fsp-fm-dot" colors={{ '--fsp-fm-dot': hex }} />;
}

/** A character's name from its id: *grandma-dot* is Grandma Dot. */
const castName = (id: string) =>
  id
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/** The steppers each movable part has: what moves, and the two presses' names and icons. */
const STEPS: Partial<
  Record<
    FaceCategory,
    { key: FaceAdjustKey; name: string; less: [string, IconName]; more: [string, IconName] }[]
  >
> = {
  eyes: [
    {
      key: 'eyesY',
      name: 'Height',
      less: ['Move the eyes up', 'up'],
      more: ['Move the eyes down', 'down'],
    },
    {
      key: 'eyesGap',
      name: 'Spacing',
      less: ['Move the eyes closer', 'shrink'],
      more: ['Move the eyes apart', 'grow'],
    },
    {
      key: 'eyesSize',
      name: 'Size',
      less: ['Make the eyes smaller', 'remove'],
      more: ['Make the eyes bigger', 'add'],
    },
  ],
  brows: [
    {
      key: 'browsY',
      name: 'Height',
      less: ['Move the brows up', 'up'],
      more: ['Move the brows down', 'down'],
    },
  ],
  nose: [
    {
      key: 'noseY',
      name: 'Height',
      less: ['Move the nose up', 'up'],
      more: ['Move the nose down', 'down'],
    },
    {
      key: 'noseSize',
      name: 'Size',
      less: ['Make the nose smaller', 'remove'],
      more: ['Make the nose bigger', 'add'],
    },
  ],
  mouth: [
    {
      key: 'mouthY',
      name: 'Height',
      less: ['Move the mouth up', 'up'],
      more: ['Move the mouth down', 'down'],
    },
    {
      key: 'mouthSize',
      name: 'Size',
      less: ['Make the mouth smaller', 'remove'],
      more: ['Make the mouth bigger', 'add'],
    },
  ],
};

// SHAKING HANDS IS A POSE FOR TWO (kit/Handshake.tsx): alone it holds a hand
// out to nobody and puts down what it holds, so the maker's moods skip it.
const MAKER_MOODS = FACE_MOODS.filter((m) => m.id !== 'shaking');

export function FaceMaker({
  opened,
  start,
  colors,
  teamName,
  others,
  keptOn = 'your team',
  othersLabel = 'Your other teams',
  onDone,
  onClose,
}: {
  opened: boolean;
  /** The team's face now, or null to open on *Start from*. */
  start: Face | null;
  /** The team's two colors, which the disc and its ring wear. */
  colors: TeamColors;
  teamName: string;
  /** Faces on my other teams, to start from. */
  others: readonly StartingFace[];
  /** Where Done keeps the face, said before leaving without it: *your team*, *your account*. */
  keptOn?: string;
  /** What Start from calls `others`: *Your other teams* from a team, *Your teams* from the account. */
  othersLabel?: string;
  onDone: (face: Face, colors: TeamColors) => void;
  onClose: () => void;
}) {
  // LEAVING WITH CHANGES ASKS FIRST, however it is asked for: Cancel, the ✕,
  // Escape or a phone's Back.
  const changed = useRef(false);
  const [leaving, setLeaving] = useState(false);
  const leave = () => (changed.current ? setLeaving(true) : onClose());
  return (
    <Workshop opened={opened} onClose={leave} title="Make a face" closeOnEscape={!leaving}>
      {opened ? (
        <MakerBody
          start={start}
          colors={colors}
          teamName={teamName}
          others={others}
          othersLabel={othersLabel}
          onDone={onDone}
          onLeave={leave}
          onChanged={(c) => {
            changed.current = c;
          }}
        />
      ) : null}
      <Dialog opened={leaving} onClose={() => setLeaving(false)} title="Leave without this face?">
        <Stack gap={3}>
          <Text>The face you made here goes when you leave. Done keeps it on {keptOn}.</Text>
          <Line gap={2} spread>
            <Button
              variant="quiet"
              onClick={() => {
                setLeaving(false);
                changed.current = false;
                onClose();
              }}
            >
              Leave it
            </Button>
            <Button variant="primary" onClick={() => setLeaving(false)}>
              Keep making it
            </Button>
          </Line>
        </Stack>
      </Dialog>
    </Workshop>
  );
}

function MakerBody({
  start,
  colors: teamColors,
  teamName,
  others,
  othersLabel,
  onDone,
  onLeave,
  onChanged,
}: {
  start: Face | null;
  colors: TeamColors;
  teamName: string;
  others: readonly StartingFace[];
  othersLabel: string;
  onDone: (face: Face, colors: TeamColors) => void;
  onLeave: () => void;
  onChanged: (changed: boolean) => void;
}) {
  // TODAY, for the seasons: a season's parts and themes are offered while it
  // is on, by the device's own date (PLAN-2026-10-24-faces-round-two §2).
  const today = useMemo(() => new Date(), []);
  // THE HISTORY: every change a step Undo can take back. A run of changes to
  // one thing (a custom color dragged across its picker) is one step.
  const [history, setHistory] = useState<{ list: Made[]; at: number; merge: string | null }>(
    () => ({
      list: [
        {
          // A garment worn at the neck before there was a body is the outfit here.
          face: start ? wearOutfit(start) : randomFace(Math.random, teamColors, { on: today }),
          colors: teamColors,
        },
      ],
      at: 0,
      merge: null,
    }),
  );
  const now = history.list[history.at]!;
  const { face, colors } = now;
  // CHANGED IS AGAINST WHERE IT STARTED, not the history's first step, which
  // a long session pushes out of the history.
  const origin = useRef(history.list[0]!).current;
  const changed = !sameFace(face, origin.face) || !sameColors(colors, origin.colors);
  useEffect(() => onChanged(changed), [changed, onChanged]);
  const apply = (next: Made, merge: string | null = null) =>
    setHistory((h) => {
      if (merge !== null && merge === h.merge && h.at === h.list.length - 1 && h.at > 0) {
        const list = h.list.slice();
        list[h.at] = next;
        return { list, at: h.at, merge };
      }
      const list = [...h.list.slice(0, h.at + 1), next].slice(-200);
      return { list, at: list.length - 1, merge };
    });
  // A FACE STARTED FROM SOMEBODY ELSE'S keeps no part its maker has not
  // earned, unless the face this opened with already wore it (§5). Only once
  // what they have earned is read: until then, or if it cannot be, the
  // server's refusal at Done is the word, rather than a crown taken off a
  // champion here.
  const setFace = (f: Face, merge: string | null = null) =>
    apply(
      {
        face:
          favorites.faces === null
            ? wearOutfit(f)
            : withoutUnearned(wearOutfit(f), favorites.earned, origin.face),
        colors,
      },
      merge,
    );
  const undo = () => setHistory((h) => ({ ...h, at: Math.max(0, h.at - 1), merge: null }));
  const redo = () =>
    setHistory((h) => ({ ...h, at: Math.min(h.list.length - 1, h.at + 1), merge: null }));

  const favorites = useFaceFavorites();
  const kept = favorites.find(face);
  const [keeping, setKeeping] = useState(false);
  const [view, setView] = useState<'start' | 'shuffle' | 'favorites' | 'parts'>(
    start === null ? 'start' : 'parts',
  );
  const [cat, setCat] = useState<FaceCategory>('head');
  // THE CHARACTER OR THE BADGE on the stage: the Outfit and Hold tabs show the
  // character as they open, the face's tabs the badge, and the switch in the
  // tools changes it whenever the person likes.
  const [figure, setFigure] = useState(false);
  const [painting, setPainting] = useState(false);
  const [paintColor, setPaintColor] = useState<string>(PAINT_COLORS[6]!.hex);
  const [dropper, setDropper] = useState(false);
  const [mood, setMood] = useState<FaceMood>('idle');
  const [theme, setTheme] = useState<FaceThemeId>('anything');
  const [locked, setLocked] = useState<ReadonlySet<FaceCategory>>(() => new Set());
  const [recent, setRecent] = useState<{ n: number; face: Face }[]>([]);
  // The face's little moment: a bounce for a fill, a spin for a shuffle.
  const [pop, setPop] = useState<{ n: number; kind: 'splash' | 'shuffle' }>({
    n: 0,
    kind: 'splash',
  });
  const stage = useRef<HTMLDivElement>(null);
  const swipe = useRef<{ x: number; y: number } | null>(null);

  /** A whole new face, as the theme and keeping what is locked, and remembered in the strip. */
  /** A face Shuffle or a mix made: on, into the strip, with a spin. */
  const landed = (next: Face) => {
    setFace(next);
    setRecent((r) => [{ n: (r[0]?.n ?? 0) + 1, face: next }, ...r].slice(0, 12));
    setPop((p) => ({ n: p.n + 1, kind: 'shuffle' }));
  };
  const shuffle = (as: FaceThemeId = theme) =>
    landed(randomFace(Math.random, colors, { theme: as, keep: { face, locked }, on: today }));
  /** A child of the face on screen and another, keeping what is locked. */
  const mix = (other: Face) =>
    landed(
      keepLocked(mixFaces(face, other, Math.random, { earned: favorites.earned }), face, locked),
    );
  const toggleLock = (c: FaceCategory) =>
    setLocked((l) => {
      const next = new Set(l);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  const themeName = faceTheme(theme).name.toLowerCase();

  // UNDO AND REDO FROM THE KEYBOARD, heard on the window rather than on the
  // maker's own box: a tap on the face leaves focus on the window's frame,
  // outside the box, where a listener on the box never hears a key.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (!mod || (key !== 'z' && key !== 'y')) return;
      e.preventDefault();
      const back = key === 'z' && !e.shiftKey;
      setHistory((h) => ({
        ...h,
        at: back ? Math.max(0, h.at - 1) : Math.min(h.list.length - 1, h.at + 1),
        merge: null,
      }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /** Fill an area with the paint can's color, or pick its color up with the dropper. */
  const fill = (slot: string) => {
    const current =
      slot === 'disc'
        ? colors.primary
        : slot === 'ring'
          ? colors.secondary
          : figureColors(face, colors)[slot as FaceArea];
    if (current === undefined) return;
    if (dropper) {
      setPaintColor(current);
      setDropper(false);
      return;
    }
    if (current.toLowerCase() === paintColor.toLowerCase()) return;
    if (slot === 'disc') apply({ face, colors: { ...colors, primary: paintColor } });
    else if (slot === 'ring') apply({ face, colors: { ...colors, secondary: paintColor } });
    else setFace(paintFace(face, slot as FaceArea, paintColor, true));
    setPop((p) => ({ n: p.n + 1, kind: 'splash' }));
  };
  const onStageClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!painting) return;
    const hit = (e.target as Element).closest('[data-slot]');
    const slot = hit?.getAttribute('data-slot');
    if (slot) fill(slot);
  };

  const savePicture = async () => {
    const svg = stage.current?.querySelector('svg');
    if (!svg) return;
    const copy = svg.cloneNode(true) as SVGSVGElement;
    // A picture shows the face at rest: the other moods' mouths are drawn and
    // hidden by the stylesheet, which a picture does not carry.
    copy
      .querySelectorAll('.fsp-bf-pleased, .fsp-bf-annoyed, .fsp-bf-open, .fsp-bf-o, .fsp-bf-snooze')
      .forEach((n) => n.remove());
    copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    // The character is taller than it is wide: the picture keeps its shape.
    const [, , vw, vh] = (copy.getAttribute('viewBox') ?? '0 0 100 100').split(' ').map(Number);
    const w = 512;
    const h = Math.round((512 * (vh ?? 100)) / (vw ?? 100));
    copy.setAttribute('width', String(w));
    copy.setAttribute('height', String(h));
    const img = new Image();
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(copy))}`;
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d')?.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/png'));
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // The team's name as typed, less what a file name cannot hold.
    a.download = `${teamName.replace(/[\\/:*?"<>|]+/g, '').trim() || 'My team'} face.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const cycleMood = () => {
    const i = MAKER_MOODS.findIndex((m) => m.id === mood);
    setMood(MAKER_MOODS[(i + 1) % MAKER_MOODS.length]!.id);
  };
  const moodName = FACE_MOODS.find((m) => m.id === mood)!.name;

  const tools = (
    <>
      <IconButton
        icon="undo"
        label="Undo"
        variant="secondary"
        disabled={history.at === 0}
        onClick={undo}
      />
      <IconButton
        icon="redo"
        label="Redo"
        variant="secondary"
        disabled={history.at === history.list.length - 1}
        onClick={redo}
      />
      <IconButton
        icon="random"
        label={`Shuffle the whole face${theme === 'anything' ? '' : ` as ${themeName}`}${locked.size > 0 ? ', keeping what is locked' : ''}`}
        variant="secondary"
        onClick={() => {
          shuffle();
          if (view === 'start') setView('parts');
        }}
      />
      <IconButton
        icon={kept ? 'favorited' : 'favorite'}
        label="Keep this face in your favorites"
        variant={kept ? 'primary' : 'secondary'}
        aria-pressed={kept !== undefined}
        disabled={keeping}
        onClick={() => {
          // FAVORITES ARE KEPT ON AN ACCOUNT, and the tour has none.
          if (!favorites.signedIn) {
            tell('Favorites are kept on an account. Sign up to keep the faces you like.');
            return;
          }
          setKeeping(true);
          void (kept ? favorites.remove(kept.id) : favorites.save(face))
            .then((done) => {
              if (done)
                tell(kept ? 'Taken out of your favorites.' : 'Kept in your favorites.', 'good');
            })
            .finally(() => setKeeping(false));
        }}
      />
      <IconButton
        icon="paint"
        label={painting ? 'Put the paint can down' : 'Pick up the paint can'}
        variant={painting ? 'primary' : 'secondary'}
        aria-pressed={painting}
        onClick={() => {
          setPainting((p) => !p);
          setDropper(false);
          setView('parts');
        }}
      />
      <IconButton
        icon="face"
        label={`Mood: ${moodName}. Show the next mood`}
        variant="secondary"
        onClick={cycleMood}
      />
      <IconButton
        icon="person"
        label="Show the whole character"
        variant={figure ? 'primary' : 'secondary'}
        aria-pressed={figure}
        onClick={() => setFigure((f) => !f)}
      />
      <IconButton
        icon="download"
        label="Save as a picture"
        variant="secondary"
        onClick={() => void savePicture()}
      />
      <IconButton
        icon="refresh"
        label="Start over from another face"
        variant="secondary"
        onClick={() => {
          setPainting(false);
          setView('start');
        }}
      />
    </>
  );

  return (
    <div className="fsp-fm">
      <div className="fsp-fm-stage">
        <div
          ref={stage}
          key={pop.n}
          className={`fsp-fm-face${figure ? ' is-figure' : ''}${painting ? ' is-painting' : ''}${dropper ? ' is-dropper' : ''}${pop.n > 0 ? ` is-${pop.kind}` : ''}`}
          role="img"
          // The face said in words (B-491), so a screen reader hears it.
          aria-label={`Your ${figure ? 'character' : 'face'}, ${moodName.toLowerCase()}: ${describeFace(face, { character: figure })}`}
          onClick={onStageClick}
          // A SWIPE ACROSS THE FACE SHUFFLES IT, unless the paint can is up,
          // when a tap is a fill. Held to the face so a swipe that ends off
          // it still counts.
          onPointerDown={(e) => {
            swipe.current = painting ? null : { x: e.clientX, y: e.clientY };
            if (!painting) e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerUp={(e) => {
            const s = swipe.current;
            swipe.current = null;
            if (!s) return;
            const dx = e.clientX - s.x;
            const dy = e.clientY - s.y;
            if (Math.abs(dx) > 40 && Math.abs(dx) > 2 * Math.abs(dy)) shuffle();
          }}
          onPointerCancel={() => {
            swipe.current = null;
          }}
        >
          {figure ? (
            <FigureArt face={face} mood={mood} team={colors} stage />
          ) : (
            <FaceArt face={face} mood={mood} disc={colors} />
          )}
        </div>
        <div className="fsp-fm-tools" role="toolbar" aria-label="Face tools">
          {tools}
        </div>
        <div className="fsp-fm-mood" aria-live="polite">
          {moodName}
        </div>
        {recent.length > 0 ? (
          <div className="fsp-fm-recent" role="group" aria-label="Recent shuffles">
            {recent.map((r) => (
              <button
                key={r.n}
                type="button"
                className="fsp-fm-recent-face"
                aria-pressed={r.face === face}
                aria-label={`Shuffled face ${r.n}: ${describeFace(r.face)}`}
                title={`Shuffled face ${r.n}`}
                onClick={() => setFace(r.face)}
              >
                <FaceArt face={r.face} disc={colors} />
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="fsp-fm-cats" role="tablist" aria-label="Parts">
        <button
          type="button"
          role="tab"
          className="fsp-fm-cat"
          aria-selected={view === 'shuffle' && !painting}
          onClick={() => {
            setView('shuffle');
            setPainting(false);
            setDropper(false);
          }}
        >
          <Icon name="random" size="sm" />
          Shuffle
        </button>
        <button
          type="button"
          role="tab"
          className="fsp-fm-cat"
          aria-selected={view === 'favorites' && !painting}
          onClick={() => {
            setView('favorites');
            setPainting(false);
            setDropper(false);
          }}
        >
          <Icon name="favorite" size="sm" />
          Favorites
        </button>
        {FACE_CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            role="tab"
            className="fsp-fm-cat"
            aria-selected={view === 'parts' && !painting && cat === c.key}
            aria-label={locked.has(c.key) ? `${c.name}, kept when shuffling` : undefined}
            onClick={() => {
              setCat(c.key);
              setView('parts');
              setPainting(false);
              setDropper(false);
              setFigure(FIGURE_CATEGORIES.has(c.key));
            }}
          >
            {c.name}
            {locked.has(c.key) ? <Icon name="locked" size="xs" /> : null}
          </button>
        ))}
      </div>

      <div
        // A PANEL OPENS AT ITS TOP: another category, or the paint can, is a
        // new panel and not the last one's scroll.
        key={painting ? 'paint' : view === 'parts' ? cat : view}
        className="fsp-fm-panel"
      >
        {view === 'start' ? (
          <StartFrom
            others={others}
            othersLabel={othersLabel}
            favorites={favorites.faces ?? []}
            colors={colors}
            today={today}
            onPick={(f) => {
              setFace(f);
              setView('parts');
            }}
            onSurprise={() => {
              shuffle();
              setView('parts');
            }}
          />
        ) : painting ? (
          <PaintPanel
            face={face}
            colors={colors}
            color={paintColor}
            dropper={dropper}
            onColor={(hex) => {
              setPaintColor(hex);
              setDropper(false);
            }}
            onDropper={() => setDropper((d) => !d)}
            onFill={fill}
            onShuffle={() =>
              setFace(shuffleFaceColors(face, Math.random, colors, { theme, locked }))
            }
          />
        ) : view === 'favorites' ? (
          <FavoritesPanel
            favorites={favorites}
            face={face}
            colors={colors}
            onPick={(f) => setFace(f)}
          />
        ) : view === 'shuffle' ? (
          <ShufflePanel
            today={today}
            theme={theme}
            locked={locked}
            colors={colors}
            mixWith={[
              ...(favorites.faces ?? []).map((f, i) => ({
                name: `Favorite ${i + 1}`,
                face: f.face,
              })),
              ...others,
              ...Object.keys(BOT_FACES).map((id) => ({
                name: castName(id),
                face: botFace(id, today)!,
              })),
            ]}
            onMix={mix}
            onTheme={(t) => {
              setTheme(t);
              shuffle(t);
            }}
            onLock={toggleLock}
            onLetGo={() => setLocked(new Set())}
          />
        ) : (
          <PartPanel
            cat={cat}
            face={face}
            colors={colors}
            theme={theme}
            today={today}
            earned={favorites.earned}
            locked={locked.has(cat)}
            onLock={() => toggleLock(cat)}
            onFace={setFace}
          />
        )}
      </div>

      <div className="fsp-fm-foot">
        <Button variant="quiet" onClick={onLeave}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => onDone(face, colors)}>
          Done
        </Button>
      </div>
    </div>
  );
}

// ── Start from ─────────────────────────────────────────────────────────────

function StartFrom({
  others,
  othersLabel,
  favorites,
  colors,
  today,
  onPick,
  onSurprise,
}: {
  others: readonly StartingFace[];
  othersLabel: string;
  /** The account's favorites, first: a face kept to wear again. */
  favorites: readonly { face: Face }[];
  colors: TeamColors;
  /** The bots wear their costumes in a season, as they do everywhere (B-490). */
  today: Date;
  onPick: (face: Face) => void;
  onSurprise: () => void;
}) {
  const choice = (s: StartingFace, key: string) => (
    <button
      key={key}
      type="button"
      className="fsp-fm-start"
      onClick={() => onPick(s.face)}
      aria-label={`Start from ${s.name}`}
    >
      <FaceArt face={s.face} disc={colors} />
      <span>{s.name}</span>
    </button>
  );
  return (
    <Stack gap={3}>
      <Line spread gap={2}>
        <h3 className="fsp-fm-head">Start from</h3>
        <Button variant="secondary" size="xs" icon="random" onClick={onSurprise}>
          Surprise me
        </Button>
      </Line>
      {favorites.length > 0 ? (
        <div>
          <div className="fsp-cap">Your favorites</div>
          <div className="fsp-fm-starts">
            {favorites.map((f, i) => choice({ name: `Favorite ${i + 1}`, face: f.face }, `f${i}`))}
          </div>
        </div>
      ) : null}
      {others.length > 0 ? (
        <div>
          <div className="fsp-cap">{othersLabel}</div>
          <div className="fsp-fm-starts">{others.map((s, i) => choice(s, `o${i}`))}</div>
        </div>
      ) : null}
      <div>
        <div className="fsp-cap">The bots</div>
        <div className="fsp-fm-starts">
          {Object.keys(BOT_FACES).map((id) =>
            choice({ name: castName(id), face: botFace(id, today)! }, id),
          )}
        </div>
      </div>
    </Stack>
  );
}

// ── Favorites ──────────────────────────────────────────────────────────────

function FavoritesPanel({
  favorites,
  face,
  colors,
  onPick,
}: {
  favorites: FaceFavoritesRead;
  face: Face;
  colors: TeamColors;
  onPick: (face: Face) => void;
}) {
  const { faces, failure, max, signedIn } = favorites;
  const current = favorites.find(face)?.id;
  return (
    <Stack gap={3}>
      <h3 className="fsp-fm-head">Favorites</h3>
      {!signedIn ? (
        <Text tone="muted" size="sm">
          Favorites are kept on an account, so they follow you to any device and any of your teams.
          Sign up to keep the faces you like.
        </Text>
      ) : faces === null ? (
        failure !== null ? (
          <Line gap={2}>
            <Text tone="muted" size="sm">
              {failure}
            </Text>
            <Button variant="secondary" size="xs" onClick={favorites.retry}>
              Try again
            </Button>
          </Line>
        ) : (
          <Text tone="muted" size="sm">
            Reading your favorites…
          </Text>
        )
      ) : faces.length === 0 ? (
        <Text tone="muted" size="sm">
          Press the star by the face to keep it here. Your favorites are kept on your account, to
          wear on any of your teams.
        </Text>
      ) : (
        <>
          <Text tone="muted" size="sm">
            {faces.length >= max
              ? 'Your favorites are full. Take one out to keep another.'
              : 'Tap one to wear it.'}
          </Text>
          <div className="fsp-fm-starts" role="group" aria-label="Your favorites">
            {faces.map((f, i) => (
              <div key={f.id} className="fsp-fm-fav">
                <button
                  type="button"
                  className="fsp-fm-start"
                  aria-pressed={current === f.id}
                  aria-label={`Wear favorite ${i + 1}: ${describeFace(f.face)}`}
                  onClick={() => onPick(f.face)}
                >
                  <FaceArt face={f.face} disc={colors} />
                </button>
                <span className="fsp-fm-fav-out">
                  <IconButton
                    icon="close"
                    size="sm"
                    label={`Take favorite ${i + 1} out of your favorites`}
                    variant="secondary"
                    onClick={() => {
                      void favorites.remove(f.id).then((gone) => {
                        // UNDO PUTS IT BACK, as a new favorite: a tap beside
                        // another face is an easy one to make by mistake.
                        if (gone)
                          tellWithUndo('Taken out of your favorites.', async () =>
                            (await favorites.save(f.face)) ? null : 'It could not be put back.',
                          );
                      });
                    }}
                  />
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </Stack>
  );
}

// ── Shuffle ────────────────────────────────────────────────────────────────

function ShufflePanel({
  today,
  theme,
  locked,
  colors,
  mixWith,
  onMix,
  onTheme,
  onLock,
  onLetGo,
}: {
  today: Date;
  theme: FaceThemeId;
  locked: ReadonlySet<FaceCategory>;
  colors: TeamColors;
  /** The faces to mix with: the favorites, your other teams', the bots'. */
  mixWith: readonly StartingFace[];
  onMix: (face: Face) => void;
  onTheme: (theme: FaceThemeId) => void;
  onLock: (category: FaceCategory) => void;
  onLetGo: () => void;
}) {
  // A face each theme made, to choose it by: the same ones each time, so the
  // grid stays still while the face above it changes.
  const themes = useMemo(() => faceThemesOn(today), [today]);
  const samples = useMemo(
    () => themes.map((t, i) => randomFace(seededRng(31 + i), colors, { theme: t.id, on: today })),
    [themes, colors, today],
  );
  return (
    <Stack gap={3}>
      <div>
        <h3 className="fsp-fm-head">Shuffle as</h3>
        <Text tone="muted" size="sm">
          Shuffle keeps shuffling as the one you choose.
        </Text>
        <div className="fsp-fm-starts" role="group" aria-label="Shuffle as">
          {themes.map((t, i) => (
            <button
              key={t.id}
              type="button"
              className="fsp-fm-start"
              aria-pressed={theme === t.id}
              onClick={() => onTheme(t.id)}
            >
              <FaceArt face={samples[i]!} disc={colors} />
              <span>{t.name}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <h3 className="fsp-fm-head">Mix with</h3>
        <Text tone="muted" size="sm">
          Your face and another make a new one, each part from one or the other. Tap again for
          another.
        </Text>
        <div className="fsp-fm-starts" role="group" aria-label="Mix with">
          {mixWith.map((m, i) => (
            <button
              key={`${m.name}-${i}`}
              type="button"
              className="fsp-fm-start"
              aria-label={`Mix with ${m.name}`}
              onClick={() => onMix(m.face)}
            >
              <FaceArt face={m.face} disc={colors} />
              <span>{m.name}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <h3 className="fsp-fm-head">Keep when shuffling</h3>
        <Text tone="muted" size="sm">
          A part you keep keeps its colors too.
        </Text>
        <div className="fsp-fm-areas" role="group" aria-label="Keep when shuffling">
          {FACE_CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              className="fsp-fm-area fsp-fm-lock"
              aria-pressed={locked.has(c.key)}
              onClick={() => onLock(c.key)}
            >
              <Icon name={locked.has(c.key) ? 'locked' : 'unlock'} size="sm" />
              {c.name}
            </button>
          ))}
        </div>
      </div>
      {locked.size > 0 ? (
        <div>
          <Button variant="quiet" size="xs" icon="unlock" onClick={onLetGo}>
            Keep nothing
          </Button>
        </div>
      ) : null}
      <Text tone="muted" size="sm">
        A swipe across the face shuffles it too.
      </Text>
    </Stack>
  );
}

// ── A category's choices, colors and steps ─────────────────────────────────

/** A category's name inside a sentence: *keep the hat*. */
const lower = (name: string) => name.toLowerCase();

function PartPanel({
  cat,
  face,
  colors,
  theme,
  today,
  earned,
  locked,
  onLock,
  onFace,
}: {
  cat: FaceCategory;
  face: Face;
  colors: TeamColors;
  theme: FaceThemeId;
  today: Date;
  /** The achievements this person holds that earn a part (§5). */
  earned: readonly string[];
  /** Whether Shuffle keeps this category. */
  locked: boolean;
  onLock: () => void;
  onFace: (face: Face, merge?: string | null) => void;
}) {
  const category = FACE_CATEGORIES.find((c) => c.key === cat)!;
  const marks = cat === 'marks';
  const full = marks && face.marks.length >= FACE_MARKS_MAX;
  const figure = FIGURE_CATEGORIES.has(cat);
  /** A category's choice now: what the character wears for the outfit, nothing held for none. */
  const chosen = (f: Face): string | undefined =>
    cat === 'body'
      ? f.body
      : cat === 'held'
        ? (f.held ?? 'none')
        : f[cat as Exclude<FaceCategory, 'marks' | 'body' | 'held'>];
  const outfit = faceOutfit(face);
  const slots = useMemo<FaceArea[]>(() => {
    // THE OUTFIT'S AREAS EVEN BEFORE ONE IS CHOSEN: the team's jersey the
    // character wears can be painted as it is.
    const ids: readonly (string | undefined)[] = marks
      ? face.marks
      : cat === 'body'
        ? [faceOutfit(face).id]
        : [chosen(face)];
    const out = new Set<FaceArea>();
    for (const id of ids) if (id) for (const s of facePart(cat, id)?.slots ?? []) out.add(s);
    return [...out];
    // `chosen` reads only `cat` and the face.
  }, [cat, face, marks]);
  const painted = figureColors(face, colors);
  // THE CHOICES ON OFFER TODAY: a season's parts while it is on, and any part
  // the face wears, so one kept after its season still shows chosen. A
  // season's come straight after None, so they are seen.
  const choices = useMemo(() => {
    const worn = (id: string) => (marks ? face.marks.includes(id as never) : chosen(face) === id);
    const offered = category.parts.filter((p) => facePartOffered(p, today, worn(p.id), earned));
    return [
      ...offered.filter((p) => p.id === 'none'),
      ...offered.filter((p) => p.earnedBy !== undefined),
      ...offered.filter((p) => p.season !== undefined),
      ...offered.filter((p) => p.id !== 'none' && p.season === undefined && !p.earnedBy),
    ];
  }, [category, cat, face, marks, today, earned]);
  // THE PARTS STILL TO EARN (§5), drawn last and locked with what earns them:
  // seeing one is what makes it worth playing for.
  const toEarn = category.parts.filter((p) => p.earnedBy !== undefined && !choices.includes(p));
  const steps = STEPS[cat] ?? [];
  const moved = steps.some((s) => face.adjust[s.key] !== 0);
  return (
    <Stack gap={3}>
      <Line spread gap={2}>
        <h3 className="fsp-fm-head">{category.name}</h3>
        <Line gap={3}>
          <IconButton
            icon={locked ? 'locked' : 'unlock'}
            label={
              locked
                ? `Let Shuffle change the ${lower(category.name)}`
                : `Keep the ${lower(category.name)} when shuffling`
            }
            variant={locked ? 'primary' : 'secondary'}
            aria-pressed={locked}
            onClick={onLock}
          />
          <Button
            variant="quiet"
            size="xs"
            icon="random"
            onClick={() => onFace(shuffleFacePart(face, cat, Math.random, { theme, on: today }))}
          >
            Shuffle
          </Button>
        </Line>
      </Line>
      {marks ? (
        <Text tone="muted" size="sm">
          Up to four at once. Choose one again to take it off.
        </Text>
      ) : null}
      {cat === 'body' && outfit.from === 'team' ? (
        <Text tone="muted" size="sm">
          {outfit.id === 'robot'
            ? 'Until you choose an outfit, your robot stands on a robot’s body and your badge shows just the face. A chosen outfit shows on the badge too.'
            : 'Until you choose one, your character wears the team’s jersey and your badge shows just the face. A chosen outfit shows on the badge too.'}
        </Text>
      ) : null}
      {/* THE NUMBER FIRST, above a long grid of outfits, where it is seen. */}
      {cat === 'body' && NUMBERED_OUTFITS.has(outfit.id) ? (
        <JerseyNumber face={face} onFace={onFace} />
      ) : null}
      <div className="fsp-fm-choices" role="group" aria-label={category.name}>
        {choices.map((p) => {
          const on = marks ? face.marks.includes(p.id as never) : chosen(face) === p.id;
          const shown = marks
            ? { ...face, marks: [p.id as never] }
            : chooseFacePart(face, cat, p.id);
          const blocked = marks && full && !on;
          const season = p.season === undefined ? null : faceSeason(p.season);
          const won = p.earnedBy !== undefined && earned.includes(p.earnedBy);
          const said = season
            ? `${p.name}, for ${season.name}`
            : won
              ? `${p.name}, earned`
              : p.name;
          return (
            <button
              key={p.id}
              type="button"
              className="fsp-fm-choice"
              aria-pressed={on}
              aria-disabled={blocked || undefined}
              aria-label={season ? said : undefined}
              title={blocked ? `${p.name}: four extras at most` : said}
              onClick={() => (blocked ? undefined : onFace(chooseFacePart(face, cat, p.id)))}
            >
              {season ? (
                <span className="fsp-fm-season" aria-hidden>
                  {season.tag}
                </span>
              ) : won ? (
                <span className="fsp-fm-season fsp-fm-earned" aria-hidden>
                  Earned
                </span>
              ) : null}
              {figure ? (
                <FigureArt face={shown} team={colors} view={FIGURE_PART_VIEW[cat]} />
              ) : (
                <FaceArt face={shown} team={colors} view={FACE_PART_VIEW[cat]} />
              )}
              <span className="fsp-fm-choice-name">{p.name}</span>
            </button>
          );
        })}
        {toEarn.map((p) => (
          <button
            key={p.id}
            type="button"
            className="fsp-fm-choice is-to-earn"
            aria-disabled
            aria-label={`${p.name}, not earned yet. Its achievement: ${p.earnedHow ?? ''}`}
            title={`${p.name}: its achievement, ${p.earnedHow ?? ''}`}
          >
            <span className="fsp-fm-to-earn" aria-hidden>
              <Icon name="locked" size="xs" />
            </span>
            {figure ? (
              <FigureArt
                face={chooseFacePart(face, cat, p.id)}
                team={colors}
                view={FIGURE_PART_VIEW[cat]}
              />
            ) : (
              <FaceArt
                face={chooseFacePart(face, cat, p.id)}
                team={colors}
                view={FACE_PART_VIEW[cat]}
              />
            )}
            <span className="fsp-fm-choice-name">{p.earnedHow}</span>
          </button>
        ))}
      </div>

      {slots.map((slot) => (
        <Swatches
          key={slot}
          label={FACE_SLOT_NAMES[slot]}
          value={painted[slot]}
          colors={swatchesFor(slot, colors)}
          onPick={(hex, merge) =>
            onFace(paintFace(face, slot, hex), merge ? `custom:${slot}` : null)
          }
        />
      ))}

      {steps.length > 0 ? (
        <div className="fsp-fm-steps">
          {steps.map((s) => {
            const [lo, hi] = FACE_ADJUST_RANGES[s.key];
            return (
              <div key={s.key} className="fsp-fm-step">
                <span className="fsp-fm-step-name">{s.name}</span>
                <IconButton
                  icon={s.less[1]}
                  label={s.less[0]}
                  variant="secondary"
                  disabled={face.adjust[s.key] <= lo}
                  onClick={() => onFace(stepFace(face, s.key, -1))}
                />
                <IconButton
                  icon={s.more[1]}
                  label={s.more[0]}
                  variant="secondary"
                  disabled={face.adjust[s.key] >= hi}
                  onClick={() => onFace(stepFace(face, s.key, 1))}
                />
              </div>
            );
          })}
          {moved ? (
            <Button
              variant="quiet"
              size="xs"
              icon="undo"
              onClick={() => {
                let f = face;
                for (const s of steps) f = stepFace(f, s.key, -f.adjust[s.key]);
                onFace(f);
              }}
            >
              Reset
            </Button>
          ) : null}
        </div>
      ) : null}
    </Stack>
  );
}

/** A face with no number on its jersey. */
function withoutNumber(face: Face): Face {
  const { number: _n, ...rest } = face;
  return rest;
}

/**
 * The number on the jersey (§2.3): typed, one or two digits as football's are,
 * `0` and `00` included; picked at random; or none. A run of typing is one
 * step of Undo.
 */
function JerseyNumber({
  face,
  onFace,
}: {
  face: Face;
  onFace: (face: Face, merge?: string | null) => void;
}) {
  return (
    <div className="fsp-fm-number">
      <TextField
        label="Number"
        code
        inputMode="numeric"
        maxLength={2}
        placeholder="—"
        value={face.number ?? ''}
        onChange={(typed) => {
          const n = typed.replace(/\D/g, '').slice(0, 2);
          onFace(n === '' ? withoutNumber(face) : { ...face, number: n }, 'number');
        }}
      />
      <Line gap={2}>
        <Button
          variant="quiet"
          size="xs"
          icon="random"
          onClick={() => onFace({ ...face, number: String(Math.floor(Math.random() * 100)) })}
        >
          Pick one
        </Button>
        {face.number !== undefined ? (
          <Button
            variant="quiet"
            size="xs"
            icon="close"
            onClick={() => onFace(withoutNumber(face))}
          >
            No number
          </Button>
        ) : null}
      </Line>
    </div>
  );
}

/** An area's swatches: the team's two colors first where a team color fits, then its palette. */
function swatchesFor(slot: FaceArea, team: TeamColors): NamedColor[] {
  const palette = paletteFor(slot);
  if (palette !== PAINT_COLORS) return [...palette];
  return [
    { name: 'Team color', hex: team.primary },
    { name: 'Team ring color', hex: team.secondary },
    ...palette,
  ];
}

function Swatches({
  label,
  value,
  colors,
  onPick,
}: {
  label: string;
  value: string;
  colors: readonly NamedColor[];
  /** `merge`: a custom color still being dragged, which is one step of Undo. */
  onPick: (hex: string, merge: boolean) => void;
}) {
  const id = `fsp-fm-sw-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div>
      <div className="fsp-field-label" id={id}>
        {label}
      </div>
      <div className="fsp-fm-swatches" role="group" aria-labelledby={id}>
        {colors.map((c, i) => (
          <button
            key={`${c.hex}-${i}`}
            type="button"
            className="fsp-fm-swatch"
            aria-label={c.name}
            title={c.name}
            aria-pressed={c.hex.toLowerCase() === value.toLowerCase()}
            onClick={() => onPick(c.hex, false)}
          >
            <Dot hex={c.hex} />
          </button>
        ))}
        <CustomColor
          label={`Any color for ${label.toLowerCase()}`}
          value={value}
          onPick={(hex) => onPick(hex, true)}
        />
      </div>
    </div>
  );
}

/** Any color at all, from the device's own picker. */
function CustomColor({
  label,
  value,
  onPick,
}: {
  label: string;
  value: string;
  onPick: (hex: string) => void;
}) {
  return (
    <label className="fsp-fm-swatch fsp-fm-custom" title={label}>
      <input
        type="color"
        aria-label={label}
        value={value}
        onChange={(e) => onPick(e.target.value)}
      />
      <span className="fsp-fm-dot fsp-fm-any" aria-hidden>
        <Icon name="add" size="sm" />
      </span>
    </label>
  );
}

// ── The paint can ──────────────────────────────────────────────────────────

function PaintPanel({
  face,
  colors,
  color,
  dropper,
  onColor,
  onDropper,
  onFill,
  onShuffle,
}: {
  face: Face;
  colors: TeamColors;
  color: string;
  dropper: boolean;
  onColor: (hex: string) => void;
  onDropper: () => void;
  onFill: (slot: string) => void;
  onShuffle: () => void;
}) {
  const rows: { name: string; colors: readonly NamedColor[] }[] = [
    {
      name: 'Team',
      colors: [
        { name: 'Team color', hex: colors.primary },
        { name: 'Team ring color', hex: colors.secondary },
      ],
    },
    { name: 'Paints', colors: PAINT_COLORS },
    { name: 'Skin', colors: SKIN_TONES },
    { name: 'Hair', colors: HAIR_COLORS },
    { name: 'Eyes', colors: EYE_COLORS },
  ];
  const areas: { slot: string; name: string }[] = [
    ...faceSlotsInUse(face).map((s) => ({ slot: s as string, name: FACE_SLOT_NAMES[s] })),
    { slot: 'disc', name: 'Badge' },
    { slot: 'ring', name: 'Ring' },
  ];
  return (
    <Stack gap={3}>
      <Line spread gap={2}>
        <h3 className="fsp-fm-head">Paint can</h3>
        <Line gap={2}>
          <span className="fsp-fm-current" aria-hidden>
            <Dot hex={color} />
          </span>
          <IconButton
            icon="dropper"
            label={dropper ? 'Put the dropper down' : 'Pick up a color from the face'}
            variant={dropper ? 'primary' : 'secondary'}
            aria-pressed={dropper}
            onClick={onDropper}
          />
        </Line>
      </Line>
      <Text tone="muted" size="sm">
        {dropper
          ? 'Tap any part of the face to pick up its color.'
          : 'Choose a color, then tap any part of the face to fill it, the badge behind it too.'}
      </Text>
      {rows.map((r) => (
        <div key={r.name}>
          <div className="fsp-cap">{r.name}</div>
          <div className="fsp-fm-swatches" role="group" aria-label={`${r.name} colors`}>
            {r.colors.map((c, i) => (
              <button
                key={`${c.hex}-${i}`}
                type="button"
                className="fsp-fm-swatch"
                aria-label={c.name}
                title={c.name}
                aria-pressed={c.hex.toLowerCase() === color.toLowerCase()}
                onClick={() => onColor(c.hex)}
              >
                <Dot hex={c.hex} />
              </button>
            ))}
            {r.name === 'Paints' ? (
              <CustomColor label="Any color" value={color} onPick={onColor} />
            ) : null}
          </div>
        </div>
      ))}
      <div>
        <div className="fsp-cap">Or fill an area</div>
        <div className="fsp-fm-areas" role="group" aria-label="Areas to fill">
          {areas.map((a) => (
            <button
              key={a.slot}
              type="button"
              className="fsp-fm-area"
              onClick={() => onFill(a.slot)}
            >
              <span className="fsp-fm-area-dot" aria-hidden>
                <Dot
                  hex={
                    a.slot === 'disc'
                      ? colors.primary
                      : a.slot === 'ring'
                        ? colors.secondary
                        : figureColors(face, colors)[a.slot as FaceArea]
                  }
                />
              </span>
              {a.name}
            </button>
          ))}
        </div>
      </div>
      <Button variant="quiet" size="xs" icon="random" onClick={onShuffle}>
        Shuffle every color
      </Button>
    </Stack>
  );
}
